// Performance optimization utilities

// Web Vitals tracking - Compatible with web-vitals v4+
export const measureWebVitals = (onPerfEntry) => {
  if (typeof window === 'undefined') return;
  
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
      onCLS(onPerfEntry);
      onINP(onPerfEntry);
      onFCP(onPerfEntry);
      onLCP(onPerfEntry);
      onTTFB(onPerfEntry);
    }).catch((error) => {
      console.log('Web Vitals not available:', error);
    });
  } else {
    // Default analytics function
    import('web-vitals').then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
      onCLS(sendToAnalytics);
      onINP(sendToAnalytics);
      onFCP(sendToAnalytics);
      onLCP(sendToAnalytics);
      onTTFB(sendToAnalytics);
    }).catch((error) => {
      console.log('Web Vitals not available:', error);
    });
  }
};

// Send performance metrics to analytics
const sendToAnalytics = (metric) => {
  // Send to your analytics service
  console.log('Performance metric:', metric);
  
  // Example: Send to Google Analytics
  if (window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
    });
  }
};

// Image optimization helpers
export const generateSrcSet = (imagePath, widths = [320, 640, 960, 1280, 1920]) => {
  const baseUrl = imagePath.split('.').slice(0, -1).join('.');
  const extension = imagePath.split('.').pop();
  
  return widths.map(width => `${baseUrl}_${width}.${extension} ${width}w`).join(', ');
};

export const generateWebPSrcSet = (imagePath, widths = [320, 640, 960, 1280, 1920]) => {
  const baseUrl = imagePath.split('.').slice(0, -1).join('.');
  
  return widths.map(width => `${baseUrl}_${width}.webp ${width}w`).join(', ');
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const resources = [
    { href: '/api/market-data', as: 'fetch', crossorigin: 'anonymous' },
    { href: '/fonts/primary.woff2', as: 'font', type: 'font/woff2', crossorigin: 'anonymous' },
  ];

  resources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    Object.assign(link, resource);
    document.head.appendChild(link);
  });
};

// Service Worker registration with updates
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none'
      });

      // Check for updates every hour
      setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000);

      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content is available, prompt user to refresh
            if (window.confirm('New version available! Refresh to update?')) {
              window.location.reload();
            }
          }
        });
      });

      return registration;
    } catch (error) {
      console.log('SW registration failed');
    }
  }
};

// Resource loading optimization
export const loadScript = (src, async = true, defer = false) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.defer = defer;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export const loadStylesheet = (href, media = 'all') => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.media = media;
    link.onload = resolve;
    link.onerror = reject;
    document.head.appendChild(link);
  });
};

// Connection speed detection
export const getConnectionSpeed = () => {
  if ('connection' in navigator) {
    return navigator.connection.effectiveType || 'unknown';
  }
  return 'unknown';
};

// Adaptive loading based on connection
export const shouldLoadHighQuality = () => {
  const connection = getConnectionSpeed();
  return ['4g', 'unknown'].includes(connection);
};

// Memory usage optimization
export const clearImageCache = () => {
  // Clear unused images from memory
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    if (!img.closest('[data-keep-in-memory]')) {
      const rect = img.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (!isVisible && img.src.startsWith('data:') || img.src.includes('blob:')) {
        img.src = '';
      }
    }
  });
};

// Bundle splitting utilities
export const loadChunk = async (chunkName) => {
  try {
    const module = await import(/* webpackChunkName: "[request]" */ `../pages/${chunkName}`);
    return module.default;
  } catch (error) {
    console.error(`Failed to load chunk: ${chunkName}`, error);
    throw error;
  }
};

// Critical CSS detection
export const getCriticalCSS = () => {
  const criticalSelectors = [
    'header', 'nav', '.hero', '.banner', 
    '.btn', '.logo', '.container', '.row', '.col',
    'h1', 'h2', 'h3', 'p'
  ];
  
  const allRules = Array.from(document.styleSheets)
    .filter(sheet => {
      try {
        return sheet.cssRules;
      } catch (e) {
        return false;
      }
    })
    .flatMap(sheet => Array.from(sheet.cssRules));

  return allRules
    .filter(rule => rule.type === CSSRule.STYLE_RULE)
    .filter(rule => criticalSelectors.some(selector => 
      rule.selectorText && rule.selectorText.includes(selector)
    ))
    .map(rule => rule.cssText)
    .join('\n');
};

// Performance budget monitoring
export const checkPerformanceBudget = () => {
  if (!window.performance) return;

  const budget = {
    FCP: 1800,  // First Contentful Paint
    LCP: 2500,  // Largest Contentful Paint
    FID: 100,   // First Input Delay
    CLS: 0.1,   // Cumulative Layout Shift
    TTFB: 800   // Time to First Byte
  };

  const navigation = performance.getEntriesByType('navigation')[0];
  
  const metrics = {
    TTFB: navigation.responseStart - navigation.fetchStart,
    domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
    loadComplete: navigation.loadEventEnd - navigation.fetchStart
  };

  Object.entries(metrics).forEach(([metric, value]) => {
    if (budget[metric] && value > budget[metric]) {
      console.warn(`Performance budget exceeded for ${metric}: ${value}ms > ${budget[metric]}ms`);
    }
  });

  return metrics;
};

// Initialize performance monitoring
export const initPerformanceMonitoring = () => {
  measureWebVitals();
  
  // Monitor resource loading
  if (window.PerformanceObserver) {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.transferSize > 500000) { // 500KB
          console.warn('Large resource detected:', entry.name, `${entry.transferSize / 1024}KB`);
        }
      });
    });
    
    observer.observe({ entryTypes: ['resource'] });
  }

  // Check performance budget after load
  window.addEventListener('load', () => {
    setTimeout(checkPerformanceBudget, 3000);
  });
};

export default {
  measureWebVitals,
  preloadCriticalResources,
  registerServiceWorker,
  loadScript,
  loadStylesheet,
  getConnectionSpeed,
  shouldLoadHighQuality,
  clearImageCache,
  loadChunk,
  getCriticalCSS,
  checkPerformanceBudget,
  initPerformanceMonitoring
};